class AudioPlayer {
  static loaded = false;

  constructor(el) {
    this.container = el;
    this.audio = el.querySelector('audio');
    this.seekSlider = el.querySelector('.c-audio-player__seek-slider');
    this.playButton = el.querySelector('.c-audio-player__button');
    this.currentTime = el.querySelector('.c-audio-player__current-time');
    this.duration = el.querySelector('.c-audio-player__duration');
    this.anchor = el.querySelector('[data-js-play-audio]');
    this.raf = null;
    this.isPlaying = false;

    this.init();
  }

  init() {
    this.handlePlayButtonClick = this.handlePlayButtonClick.bind(this);
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.handleProgressBarChange = this.handleProgressBarChange.bind(this);
    this.handleAudioLoaded = this.handleAudioLoaded.bind(this);
    this.handleSeekChange = this.handleSeekChange.bind(this);
    this.whilePlaying = this.whilePlaying.bind(this);
    this.handleAudioEnd = this.handleAudioEnd.bind(this);
    this.events();

    // In case audio has already been loaded before event listener is attached
    if (this.audio.readyState > 0) {
      this.displayAudioDuration();
      this.setSliderMax();
    } else {
      this.audio.addEventListener('loadedmetadata', this.handleAudioLoaded);
    }
  }

  events() {
    this.playButton.addEventListener('click', this.handlePlayButtonClick);
    this.seekSlider.addEventListener('input', this.handleProgressBarChange);
    this.seekSlider.addEventListener('change', this.handleSeekChange);
    this.audio.addEventListener('ended', this.handleAudioEnd);
    this.anchor &&
      this.anchor.addEventListener('click', this.handlePlayButtonClick);
    this.container.addEventListener('play-audio', () => this.play());
    this.container.addEventListener('pause-audio', () => this.pause());
  }

  handlePlayButtonClick() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  play() {
    this.isPlaying = true;
    this.container.classList.add('c-audio-player--is-playing');
    this.playButton.querySelector('.c-audio-player__button-label').innerHTML =
      'Pause';
    this.audio.play();
    requestAnimationFrame(this.whilePlaying);
  }

  pause() {
    this.isPlaying = false;
    this.container.classList.remove('c-audio-player--is-playing');
    this.playButton.querySelector('.c-audio-player__button-label').innerHTML =
      'Play';
    this.audio.pause();
    cancelAnimationFrame(this.raf);
  }

  handleProgressBarChange() {
    this.container.style.setProperty(
      '--seek-before-width',
      `${(this.seekSlider.value / this.seekSlider.getAttribute('max')) * 100}%`,
    );
    this.seekSlider.setAttribute('aria-valuenow', this.seekSlider.value);
    if (this.isPlaying) {
      cancelAnimationFrame(this.raf);
    }
  }

  handleAudioLoaded() {
    this.setSliderMax();
    this.displayAudioDuration();
  }

  handleSeekChange() {
    this.currentTime.innerHTML = this.calculateTime(this.seekSlider.value);
    this.audio.currentTime = this.seekSlider.value;
    if (this.isPlaying) {
      requestAnimationFrame(this.whilePlaying);
    }
  }

  handleAudioEnd() {
    this.pause();
  }

  displayAudioDuration() {
    const duration = this.audio.duration;
    this.duration.innerHTML = this.calculateTime(duration);
  }

  calculateTime(secs) {
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : seconds;
    return `${minutes}:${returnedSeconds}`;
  }

  setSliderMax() {
    this.seekSlider.setAttribute('max', Math.floor(this.audio.duration));
    this.seekSlider.setAttribute(
      'aria-valuemax',
      Math.floor(this.audio.duration),
    );
  }

  whilePlaying() {
    this.seekSlider.value = Math.floor(this.audio.currentTime);
    this.currentTime.innerHTML = this.calculateTime(this.audio.currentTime);
    this.handleProgressBarChange();
    this.raf = requestAnimationFrame(this.whilePlaying);
  }
}

export function init() {
  if (AudioPlayer.loaded) {
    return;
  }

  const audioPlayers = Array.from(
    document.querySelectorAll('[data-js-audio-player]'),
  );

  audioPlayers.length && audioPlayers.forEach((el) => new AudioPlayer(el));

  AudioPlayer.loaded = true;
}
